// src/pages/OrdersPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useApp } from '../context/AppContext';
import { Package, ChevronDown, ChevronUp, ShoppingBag, Clock, CheckCircle, Truck, FileText, RefreshCw } from 'lucide-react';
import { generateInvoice } from '../utils/pdfGenerator';
import Loader from '../components/Loader';

const STATUS_CONFIG = {
  confirmed:  { label: 'Confirmed',  color: 'bg-blue-100 text-blue-600',   Icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-600', Icon: Clock       },
  shipped:    { label: 'Shipped',    color: 'bg-purple-100 text-purple-600',Icon: Truck       },
  delivered:  { label: 'Delivered',  color: 'bg-green-100 text-green-600', Icon: CheckCircle },
  cancelled:  { label: 'Cancelled',  color: 'bg-red-100 text-red-600',     Icon: Package     },
};

function OrderCard({ order }) {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.confirmed;
  const { Icon } = status;
  const date = order.createdAt?.toDate?.()?.toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  }) || 'Just now';

  return (
    <div className="bg-white rounded-3xl shadow-soft overflow-hidden">
      {/* Header */}
      <div className="p-5 flex items-center justify-between cursor-pointer hover:bg-rose-50/40 transition-colors"
        onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-rose-50 rounded-2xl flex items-center justify-center">
            <Package size={22} className="text-rose-400" />
          </div>
          <div>
            <p className="font-body font-semibold text-gray-900 text-sm">Order #{order.id.slice(-8).toUpperCase()}</p>
            <p className="text-xs text-gray-400 font-body mt-0.5">{date} · {order.items?.length} item(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <p className="font-display font-bold text-gray-900">₹{order.total?.toLocaleString('en-IN')}</p>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${status.color}`}>
              <Icon size={10} className="inline mr-1" />{status.label}
            </span>
          </div>
          {expanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
        </div>
      </div>

      {/* Mobile status */}
      <div className="px-5 pb-3 sm:hidden flex items-center justify-between">
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${status.color}`}>{status.label}</span>
        <p className="font-display font-bold text-gray-900">₹{order.total?.toLocaleString('en-IN')}</p>
      </div>

      {/* Expanded */}
      {expanded && (
        <div className="border-t border-rose-100 p-5 animate-slide-up">
          {/* Items */}
          <h4 className="font-body font-semibold text-gray-700 text-sm mb-3">Items Ordered</h4>
          <div className="space-y-3 mb-5">
            {order.items?.map((item, i) => (
              <div key={i} className="flex gap-3 items-center bg-rose-50/50 rounded-2xl p-3">
                <img src={item.image} alt={item.name} className="w-14 h-14 object-cover rounded-xl flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-body font-medium text-gray-900 text-sm line-clamp-1">{item.name}</p>
                  <p className="text-xs text-gray-400 font-body">Qty: {item.quantity} × ₹{item.price?.toLocaleString('en-IN')}</p>
                </div>
                <p className="font-body font-semibold text-gray-900 text-sm">
                  ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                </p>
              </div>
            ))}
          </div>

          {/* Shipping */}
          {order.shippingAddress && (
            <div className="mb-5">
              <h4 className="font-body font-semibold text-gray-700 text-sm mb-2">Deliver to</h4>
              <div className="bg-rose-50/50 rounded-2xl p-3">
                <p className="font-body text-sm text-gray-800 font-medium">{order.shippingAddress.name}</p>
                <p className="font-body text-xs text-gray-500 mt-0.5">
                  {order.shippingAddress.address}, {order.shippingAddress.city}, {order.shippingAddress.state} — {order.shippingAddress.pincode}
                </p>
                <p className="font-body text-xs text-gray-500">{order.shippingAddress.phone}</p>
              </div>
            </div>
          )}

          {/* Courier info */}
          {order.courierName && (
            <div className="mb-5 bg-blue-50 border border-blue-100 rounded-2xl p-3">
              <p className="text-xs text-blue-600 font-body font-semibold mb-1">🚚 Shipping Info</p>
              <p className="text-sm text-gray-700 font-body">Courier: <span className="font-semibold">{order.courierName}</span></p>
              {order.awbCode && <p className="text-xs text-gray-500 font-body mt-0.5">AWB: {order.awbCode}</p>}
            </div>
          )}

          {/* Price */}
          <div className="border-t border-rose-100 pt-4 space-y-1.5">
            <div className="flex justify-between text-sm font-body text-gray-500">
              <span>Subtotal</span><span>₹{order.subtotal?.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm font-body text-gray-500">
              <span>Delivery</span><span className="text-green-500 font-medium">FREE</span>
            </div>
            <div className="flex justify-between font-display font-bold text-gray-900 pt-1.5 border-t border-rose-50">
              <span>Total Paid</span><span>₹{order.total?.toLocaleString('en-IN')}</span>
            </div>
          </div>

          {order.paymentId && (
            <p className="text-xs text-gray-400 font-body mt-3">
              Payment ID: <span className="font-mono">{order.paymentId}</span>
            </p>
          )}

          {/* Track Button */}
          <Link to={`/track/${order.id}`}
            className="mt-5 w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-3 rounded-2xl font-body font-medium text-sm transition-colors">
            <Truck size={16} /> Track This Order
          </Link>

          {/* Invoice + Return */}
          <div className="flex gap-2 mt-2">
            <button onClick={() => generateInvoice(order)}
              className="flex-1 flex items-center justify-center gap-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 py-2.5 rounded-2xl font-body font-medium text-xs transition-colors border border-gray-200">
              <FileText size={14} /> Download Invoice
            </button>
            {order.status === 'delivered' && (
              <Link to="/returns"
                className="flex-1 flex items-center justify-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-600 py-2.5 rounded-2xl font-body font-medium text-xs transition-colors border border-amber-200">
                <RefreshCw size={14} /> Return / Refund
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrdersPage() {
  const { user } = useApp();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchOrders = async () => {
      try {
        const q    = query(collection(db, 'orders'), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const da = a.createdAt?.toDate?.() || new Date(0);
          const db_ = b.createdAt?.toDate?.() || new Date(0);
          return db_ - da;
        });
        setOrders(list);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user]);

  if (!user) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-cream pt-20">
      <div className="text-5xl mb-2">🔒</div>
      <h2 className="font-display font-bold text-2xl text-gray-800">Login to view orders</h2>
      <Link to="/login" className="bg-rose-500 text-white px-6 py-3 rounded-2xl hover:bg-rose-600 transition-colors font-medium">
        Login / Sign Up
      </Link>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream pt-20 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-rose-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
              {user.displayName?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl text-gray-900">
                Hey, {user.displayName?.split(' ')[0]}! 👋
              </h1>
              <p className="text-gray-400 font-body text-sm">{user.email}</p>
            </div>
          </div>
        </div>

        <div>
          <h2 className="font-display font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Package size={22} className="text-rose-400" /> My Orders
          </h2>

          {loading ? (
            <div className="flex justify-center py-16"><Loader size="lg" /></div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-3xl shadow-soft p-12 text-center">
              <div className="w-20 h-20 bg-rose-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag size={36} className="text-rose-300" />
              </div>
              <h3 className="font-display font-semibold text-xl text-gray-700 mb-2">No orders yet</h3>
              <p className="text-gray-400 font-body text-sm mb-6">Start gifting love to your partner 💕</p>
              <Link to="/products"
                className="inline-block bg-rose-500 text-white px-8 py-3.5 rounded-2xl font-medium hover:bg-rose-600 transition-colors shadow-rose">
                Shop Now
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => <OrderCard key={order.id} order={order} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
